import { BlogCard } from './types';

export const SunBurnInChildrenBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Sunburn in Children: Treatment, Prevention and When to Seek Help',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },

      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black ',
      },
    },
    description: {
      content:
        'This guide covers what sunburn in children looks like, how to treat child sunburn at home, how to protect your child’s skin effectively and the warning signs that mean your child needs to be seen by a doctor.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'sunburn-hero-blog.webp',
    bannerUrl: 'sunburn-hero-blog.webp',
    slug: 'sun-burn-in-children',
    contentType: 'article',
    doctorSlug: 'emanuela-manea',
    bannerDescription: `Sunburn in children can happen faster than most parents realise, sometimes within as little as ten to fifteen minutes during summer. Despite this, it remains one of the most preventable causes of long-term skin damage in childhood.\n\nThis guide covers what sunburn in children looks like, how to treat child sunburn at home, how to protect your child’s skin effectively and the warning signs that mean your child needs to be seen by a doctor.`,
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What Does Sunburn in Children Look Like?',
          id: 'what-does-sunburn-in-children-look-like',
        },
        {
          text: 'Which Children Are Most at Risk of Sunburn?',
          id: 'which-children-are-most-at-risk-of-sunburn',
        },
        {
          text: 'How to Treat Sunburn in a Child at Home',
          id: 'how-to-treat-sunburn-in-a-child-at-home',
        },
        {
          text: 'How to Protect Your Child from Sunburn',
          id: 'how-to-protect-your-child-from-sunburn',
        },
        {
          text: 'When Should You Seek Medical Help for Sunburn in Children?',
          id: 'when-should-you-seek-medical-help-for-sunburn-in-children',
        },
        {
          text: 'The Long-Term Risks of Sunburn in Childhood',
          id: 'the-long-term-risks-of-sunburn-in-childhood',
        },
        {
          text: 'Sunburn in Children: Frequently Asked Questions',
          id: 'sunburn-in-children-frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    // topics: {
    //   title: {
    //     content: '#### TOPICS:',
    //     styles: {
    //       h4: 'text-theme-primary font-extrabold p-2',
    //     },
    //   },
    //   topics: ['Colic in Babies', 'Paediatrics'],
    //   styles: {
    //     p: 'p-2 bg-info-500-opac-10 text-teal-450 bg-info-500-soft',
    //   },
    // },
    speciality: 'Paediatrics',
    iconUrl: 'emanuela-manea.webp',
    date: {
      content: '27, July 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    postimonial: {
      content: 'BSc, MBChB, FRCS (Tr&Orth) \n Consultant Paediatrician',
      styles: {
        p: 'text-xl whitespace-pre-line',
      },
    },
    author: {
      content: 'Dr Emanuela Manea',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    content: {
      content: `
<span id="what-does-sunburn-in-children-look-like"></span>
### What Does Sunburn in Children Look Like?

Sunburn is skin damage caused by excessive exposure to ultraviolet (UV) radiation. Symptoms usually begin two to six hours after sun exposure and typically peak between twelve and twenty-four hours later, which means a child who seemed fine at the beach may develop significant symptoms overnight.

<div align="center">\n\n
![Free skincare day at Battersea Power Station](sunburn-first-image.webp "1024x400")\n\n
</div>

#### In Children with Lighter Skin

In children with fair or lighter skin tones, sunburn typically causes:

- Redness (erythema) that appears within a few hours of sun exposure
- Skin that feels hot or tender to the touch
- Swelling of affected areas
- Itching, pain or soreness
- Peeling as the skin heals over the following days
- Blistering in more severe cases

#### In Children with Darker Skin

Sunburn in children with darker skin tones is often less obvious because redness can be difficult to see. Parents should look for:

- Skin that feels unusually hot or sensitive to touch
- Itching or discomfort
- Flaking or peeling skin in the days that follow
- Hyperpigmentation, which is darker patches or spots appearing after the inflammation settles

<span style="font-weight: bold">Important:</span> children with darker skin are still at risk from UV damage and skin cancer. All children regardless of skin tone need adequate sun protection.

#### Severe Sunburn in Children

More severe cases of sunburn in children may cause:

- Extensive blistering across different body areas
- Significant pain that interferes with sleep or daily activities
- Chills or shivering
- Fever
- Nausea or vomiting
- Headache or dizziness
- General malaise or unusual lethargy

<div class="bg-surface-secondary border border-border-default rounded-lg p-4 my-6">
Severe sunburn with fever, vomiting, dizziness or significant lethargy may indicate heat-related illness or heatstroke, which is a medical emergency. Call 999 or go directly to the nearest emergency department.
</div>

<span id="which-children-are-most-at-risk-of-sunburn"></span>

### Which Children Are Most at Risk of Sunburn?

Not all children have the same risk of sunburn. The risk depends largely on a child’s skin phototype, which categorises skin by its natural colour and its response to UV radiation.

| Skin Type | Typical Features | Sun Response | New Column |
|-----------|------------------|--------------|------------|
| Type I | Pale white, blue or hazel eyes, blond or red hair | Always burns, never tans | Very High |
| Type II | Fair skin, blue eyes | Burns easily, tans poorly | High |
| Type III | Darker white skin | Tans after initial burn | Moderate |
| Type IV | Light brown skin | Burns mininally, tans easily | Low |
| Type V | Brown skin | Rarely burns, tans darkly | Very Low |
| Type VI | Dark brown or black skin | Never burns, always tans darkly | Minimal |

Children with Type I and Type II skin are at the greatest risk of sunburn and should take the most careful precautions. However, all children benefit from sun protection, and darker skin types are not immune to UV damage or skin cancer.

<div align="center">\n\n
![Free skincare day at Battersea Power Station](sunburn-second-image.webp "1024x400")\n\n
</div>

A child with very fair skin may develop sunburn after as little as ten to fifteen minutes of midday sun exposure in the UK during peak summer. A child with darker skin may tolerate considerably longer exposure before burning, but the cumulative long-term risk of UV damage remains.

#### Can Children Get Sunburn on Cloudy Days?

Yes. Up to 80 percent of UV radiation reaches the earth on overcast days. UV rays also reflect off water, sand, snow and light-coloured surfaces, which can increase exposure significantly. Parents often underestimate the risk on cooler or overcast days, particularly during UK summers when bright sunshine is not always guaranteed.

<span id="how-to-treat-heat-rash-in-children-at-home"></span>

### How to Treat Sunburn in a Child at Home

Most cases of sunburn in children can be managed at home with straightforward first aid. The aims of treatment are to cool the skin, relieve discomfort and allow healing without further damage.

<div style="display:flex;flex-wrap:wrap;justify-content:center;gap:20px;margin:32px 0;">

  <div style="flex:1 1 320px;max-width:360px;background:#fff;border:1px solid #D9D9D9;border-radius:12px;padding:20px;">
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">
      Immediate Steps
    </h4>
    <ul style="margin:0;padding-left:20px;font-size:12px;line-height:1.7;">
      <li style="font-size:12px; color:#939393;">Move your child out of the sun immediately and into a cool, shaded environment.</li>
      <li style="font-size:12px; color:#939393;">Avoid any further sun exposure until the skin has fully healed.</li>
      <li style="font-size:12px; color:#939393;">Encourage rest.</li>
      <li style="font-size:12px; color:#939393;">Offer extra fluids to prevent dehydration, particularly in babies and young children.</li>
    </ul>
  </div>

  <div style="flex:1 1 320px;max-width:360px;background:#fff;border:1px solid #D9D9D9;border-radius:12px;padding:20px;">
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">
      Cooling the Skin
    </h4>
    <ul style="margin:0;padding-left:20px;font-size:12px;line-height:1.7;">
      <li style="font-size:12px; color:#939393;">Apply cool, damp compresses to the affected areas or simply spray clean water on the skin if touching is not tolerated.</li>
      <li style="font-size:12px; color:#939393;">Give a cool bath or shower, avoiding very cold water which can cause additional shock to the skin.</li>
      <li style="font-size:12px; color:#939393;">Do not apply ice directly to sunburned skin as this can cause further damage.</li>
    </ul>
  </div>

  <div style="flex:1 1 320px;max-width:360px;background:#fff;border:1px solid #D9D9D9;border-radius:12px;padding:20px;">
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">
      Pain Relief
    </h4>
    <p style="margin:0 0 10px;font-size:12px;line-height:1.6; color:#939393;">
      If your child is uncomfortable from sunburn, consider:
    </p>
    <ul style="margin:0;padding-left:20px;font-size:12px;line-height:1.7;">
      <li style="font-size:12px; color:#939393;">Paracetamol or ibuprofen at the appropriate age-based dose for pain and inflammation.</li>
      <li style="font-size:12px; color:#939393;">A mild topical corticosteroid cream applied twice daily to help reduce swelling, itching and discomfort; avoid applying on blistered areas.</li>
      <li style="font-size:12px; color:#939393;">Ask a pharmacist for guidance on the most appropriate products if you are unsure.</li>
    </ul>
  </div>

  <div style="flex:1 1 320px;max-width:360px;background:#fff;border:1px solid #D9D9D9;border-radius:12px;padding:20px;">
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">
      Moisturising Sunburned Skin
    </h4>
    <ul style="margin:0;padding-left:20px;font-size:12px;line-height:1.7;">
      <li style="font-size:12px; color:#939393;">Apply a fragrance-free mild moisturiser or aloe vera gel to soothe dry, irritated skin.</li>
      <li style="font-size:12px; color:#939393;">Reapply regularly while the skin is healing.</li>
      <li style="font-size:12px; color:#939393;">Avoid petroleum jelly (Vaseline) immediately after sunburn as it traps heat in the skin.</li>
      <li style="font-size:12px; color:#939393;">Although aloe vera feels soothing, there is no strong scientific evidence it speeds healing. It is safe to use and may provide comfort.</li>
    </ul>
  </div>

  <div style="flex:1 1 320px;max-width:360px;background:#fff;border:1px solid #D9D9D9;border-radius:12px;padding:20px;">
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;">
      Blister Care
    </h4>
    <p style="margin:0 0 10px;font-size:12px;line-height:1.6; color:#939393;">
      If your child has sunburn blisters:
    </p>
    <ul style="margin:0;padding-left:20px;font-size:12px;line-height:1.7;">
      <li style="font-size:12px; color:#939393;">Leave blisters intact wherever possible.</li>
      <li style="font-size:12px; color:#939393;">Do not deliberately pop or pick at blisters.</li>
      <li style="font-size:12px; color:#939393;">Keep the area clean and covered.</li>
      <li style="font-size:12px; color:#939393;">Seek medical advice if blisters are large, very painful or show signs of infection such as increasing redness, warmth or pus.</li>
    </ul>
  </div>

</div>

<span id="sun-allergy-in-children-what-is-polymorphic-light-eruption"></span>

### How to Protect Your Child from Sunburn?

The best treatment for sunburn in children is prevention. When the UV Index is 3 or above, which in the UK occurs regularly from April through to September, sun protection should be consistent and layered rather than relying on sunscreen alone.

<div align="center">\n\n
![Free skincare day at Battersea Power Station](sunburn-third-image.webp "1024x400")\n\n
</div>

The five SunSmart steps provide a practical framework:

#### 1. Slip on Protective Clothing

Clothing is one of the most reliable forms of sun protection for children, particularly young children who may be reluctant to keep sunscreen on.

- Choose lightweight, loose-fitting fabrics such as cotton or linen
- Look for clothing with a UPF (Ultraviolet Protection Factor) rating
- Cover arms, legs and the back of the neck where possible

#### 2. Slop on the Sunscreen

When choosing and applying sunscreen for children:

- Use a broad-spectrum sunscreen that protects against both UVA and UVB radiation
- Choose SPF of at least 15+ (pending on your child’s skin needs)
- Apply sunscreen to all exposed skin including face, ears, neck, arms, hands and legs
- Reapply after swimming, sweating or towel drying or as needed
- Creams and lotions generally provide more reliable coverage than sprays
- Remember that no sunscreen can provide 100 per cent protection from the sun, so this should never be solely relied on.

How much sunscreen does a child actually need? Many parents apply far less than is required for effective protection. Use these approximate amounts as a guide:

| Child's Age | Approximate Amount of Sunscreen Needed |
|-----------|------------------|
| 2 years | 2 teaspoons |
| 5 years | 3 teaspoons |
| 10 years | 5 teaspoons |
| Older children | 6 to 7 teaspoons |

##### Babies under 12 months

Whenever possible, keep babies out of direct sunlight, particularly during the hottest parts of the day. They can still enjoy time outdoors, including walks in the morning and late afternoon when UV rays are less intense. Watch for signs of overheating, discomfort, or distress. Protective clothing, wide-brimmed hats, and shade should be used as the main forms of sun protection. Sunscreen is generally not recommended for infants under 12 months of age as babies have a higher surface-area to body-weight ratio compared with older children and adults, which means that a baby’s exposure to the chemicals in sunscreens is greater, possibly increasing the risk of an unwanted reaction. If you need it, consider sunscreens with physical blocks like titanium dioxide or zinc oxide are both gentle and effective for sensitive areas such as the nose, cheeks, tops of the ears and the shoulders.

#### 3. Slap on a Hat

Hats protect the face, ears and neck, three areas that are frequently sunburned but difficult to cover with sunscreen alone.

- Choose broad-brimmed hats to cover your child’s face, eyes, ears and the back of their neck
- Baseball caps and visors do not provide adequate protection for the ears and neck
- For babies, elasticated hat straps help keep them in place

#### 4. Seek Shade

- Stay under trees, gazebos, shelters or umbrellas during peak UV hours
- In the UK, peak UV is typically between 11am and 3pm in summer
- Remember that UV radiation can still reach the skin in the shade and reflected UV from sand and water increases exposure

####5. Slide on Sunglasses

- Use wrap-around sunglasses with UV400 or Category 3 to 4 UV-protective lenses
- For babies and toddlers, look for sunglasses with elastic straps to keep them in place
- Protecting children’s eyes from UV exposure from an early age could help reducing the long-term risk of cataracts and other eye conditions

<span style="font-weight: bold;">A note on tanning:</span> a tan is the skin’s visible response to UV-induced injury. There is no such thing as a safe tan. Tanning in children is a sign of skin damage, not health, and parents should not aim to achieve a gradual tan as a form of protection.

<span id="how-is-sun-allergy-different-from-heat-rash"></span>

### When Should You Seek Medical Help for Sunburn in Children?

<div align="center">\n\n
![Free skincare day at Battersea Power Station](sunburn-fourth-image.webp "1024x400")\n\n
</div>

<div class="bg-surface-secondary border border-border-default rounded-lg p-4 my-6">
Call 999 or go to the nearest emergency department if your child has a high fever, is unusually sleepy or difficult to rouse, is vomiting, has a severe headache or dizziness, shows signs of significant dehydration, or has extensive blistering. These symptoms may indicate heatstroke or severe heat-related illness, which requires emergency treatment.
</div>

See a doctor or paediatrician promptly if:

- The sunburn is painful or covers a large area of the body
- Your child is a baby under twelve months with any degree of sunburn
- Blisters involve different body parts, are painful or show signs of infection
- The sunburn is not improving after a few days of home treatment
- Your child has an underlying skin condition such as eczema that may be complicated by sunburn
- You are concerned or uncertain about your child’s condition

### The Long-Term Risks of Sunburn in Childhood

A single episode of sunburn in childhood is not a cause for alarm. However, repeated sunburn during childhood and adolescence may contribute to the risk of long-term skin damage and skin cancer in later life.

The skin damage from sunburn is cumulative. Even without visible burning, repeated sun exposure that does not cause immediate redness still contributes to premature skin ageing, pigmentation changes and an increased risk of melanoma and other skin cancers in adulthood.

This is one of the reasons paediatricians increasingly discuss sun protection as a routine part of child health advice, particularly for children with fair skin types.

<span id="sunburn-in-children-frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'How long does sunburn last in children?',
          },
          answer: {
            content:
              'Most mild sunburn in children heals within three to five days. More severe sunburn with blistering or peeling may take one to two weeks to heal fully. Keeping the skin moisturised, out of the sun and well hydrated supports the healing process. If sunburn is not improving after a week, or your child develops signs of infection, seek medical review.',
          },
        },
        {
          question: {
            content:
              'What is the difference between sunburn and heat rash in children?',
          },
          answer: {
            content:
              'Sunburn vs heat rash is a common source of confusion in summer. Sunburn is caused by UV radiation and develops on directly sun-exposed skin, appearing two to six hours after exposure. Heat rash is caused by blocked sweat ducts during overheating and tends to appear where the skin is covered or in body folds rather than on sun-exposed areas. You can read more about heat rash in our heat rash in children guide.',
          },
        },
        {
          question: {
            content: 'Should I pop sunburn blisters on my child?',
          },
          answer: {
            content:
              'No. Sunburn blisters on children should be left intact wherever possible. Blisters form as a protective response to skin damage and popping them increases the risk of infection and scarring. Keep the area clean, apply a non-adhesive dressing if needed, and seek medical advice if blisters are large or becoming infected.',
          },
        },
        {
          question: {
            content: 'What is the best sunscreen for children in the UK?',
          },
          answer: {
            content: `The best sunscreen for children in the UK is a broad-spectrum SPF 15 or higher product that protects against both UVA and UVB radiation and is water-resistant for outdoor activity. For young children and babies, mineral-based sunscreens containing zinc oxide or titanium dioxide are generally well tolerated on sensitive skin.`,
          },
        },
        {
          question: {
            content: 'Can you get heatstroke from sunburn?',
          },
          answer: {
            content:
              'Sunburn itself does not cause heatstroke, but the two can occur together in hot conditions. Heatstroke is a medical emergency caused by the body’s core temperature rising to dangerous levels, usually after prolonged exposure to heat. Signs include a very high temperature, confusion, not sweating despite the heat, rapid breathing and loss of consciousness. If you suspect heatstroke, call 999 immediately.',
          },
        },
        {
          question: {
            content:
              'When should I take my child to a paediatrician after sunburn?',
          },
          answer: {
            content:
              'See a paediatrician if your child has severe sunburn, is a baby under twelve months, has significant blistering, or if you have any concerns about their recovery. A private paediatric consultation can provide prompt assessment, specific guidance for your child’s skin type and referral to a dermatologist if needed. Appointments are available across London clinics and online.',
          },
        },
      ],
    },
    postFaqContent: {
      content: `
      <div class="bg-surface-secondary border border-border-default rounded-lg p-4 my-6">
        If your child has had significant sunburn, or if you have concerns about their skin during summer, I am here to help. Appointments are available at clinics across London including Canary Wharf, Battersea and Holborn, and online. You can find out more on the How Appointments Work page or request a consultation directly.
      </div>\n\n
><span style="font-weight: bold; color: #2A347A">ⓘ Disclaimer:</span>
>
>The content on this blog is written by Dr Emanuela Manea, Consultant Paediatrician, and is intended to provide general health information for parents and carers. It does not constitute medical advice and should not be used to diagnose, treat or manage any medical condition. Every child is different, and clinical findings and treatment decisions should always be made in the context of an individual assessment by a qualified healthcare professional. If you have concerns about your child’s health, please seek appropriate medical advice promptly. If your child is seriously unwell, call 999 or go to your nearest emergency department.
`,
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        h3: 'text-lg md:text-xl font-bold text-theme-primary mt-8 mb-4',
        a: 'text-text-action font-bold',
        em: 'italic',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 rounded-lg font-bold no-underline cursor-pointer mt-8 py-3 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-secondary border border-blue-200 rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
    },
  },
];
